// Farming unit editor (New Season): fields & blocks list + map.
// "Save changes" sends the full desired layout to PUT v2/.../layout and then
// re-reads the tree (see newSeasonActions.save_new_season_layout).
const React = require('react');
const { useState, useEffect, useMemo, useCallback, useRef } = require('react');
const { useDispatch, useSelector } = require('react-redux');
const { useTranslation } = require('react-i18next');
const kmlManager = require('kmlManager');
const { save_new_season_layout } = require('@actions/newSeasonActions');
const layout = require('./layoutHelpers');
const { findV2FarmingUnit, farmingUnitTitle } = require('./v2Distributions');
const BlockList = require('./BlockList');
const BlocksMap = require('./BlocksMap');
const FarmingUnitFormModal = require('./FarmingUnitFormModal');
const {
  ConfirmDeleteBlockModal,
  useToast,
  Toast,
  SegmentedTabs,
} = require('./NewSeasonUi');

const WRITE_ROLES = ['admin', 'moderator'];

// The farming unit from GET v2/distributions (never the V1 store).
const useFarmingUnit = (distributionId, farmingUnitId) => {
  const distributions = useSelector(
    (state) => state.new_season_data?.distributions,
  );
  return findV2FarmingUnit(distributions, distributionId, farmingUnitId);
};

const isEditingText = (target) =>
  ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName) ||
  target?.isContentEditable;

// The draft layout, reset whenever a new tree arrives (load or after save).
const useDraft = (tree, setDirty) => {
  const [draft, setDraftState] = useState(() => layout.draftFromTree(tree));
  useEffect(() => {
    setDraftState(layout.draftFromTree(tree));
    setDirty(false);
  }, [tree, setDirty]);
  const updateDraft = useCallback(
    (update) => {
      setDraftState((previous) => update(previous));
      setDirty(true);
    },
    [setDirty],
  );
  return [draft, updateDraft];
};

const useSelection = () => {
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [selectedFieldKey, setSelectedFieldKey] = useState(null);
  const selectBlock = useCallback((key, event) => {
    setSelectedFieldKey(null);
    const isMulti = event?.shiftKey || event?.metaKey || event?.ctrlKey;
    setSelectedKeys((previous) => {
      if (!isMulti) {
        return [key];
      }
      return previous.includes(key)
        ? previous.filter((current) => current !== key)
        : previous.concat([key]);
    });
  }, []);
  const selectField = useCallback((key) => {
    setSelectedKeys([]);
    setSelectedFieldKey(key);
  }, []);
  const clear = useCallback(() => {
    setSelectedKeys([]);
    setSelectedFieldKey(null);
  }, []);
  return {
    selectedKeys,
    setSelectedKeys,
    selectedFieldKey,
    selectBlock,
    selectField,
    clear,
  };
};

// Delete/Backspace on a single selected block (outside text inputs) asks to delete it.
const useDeleteKey = (selectedKeys, canEdit, onRequestDelete) => {
  useEffect(() => {
    const onKeyDown = (event) => {
      if (
        !['Delete', 'Backspace'].includes(event.key) ||
        isEditingText(event.target)
      ) {
        return;
      }
      if (canEdit && selectedKeys.length === 1) {
        event.preventDefault();
        onRequestDelete(selectedKeys[0]);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [selectedKeys, canEdit, onRequestDelete]);
};

const KmzDropZone = ({ disabled, onAreas }) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const readFiles = (files) => {
    if (!disabled && files?.length) {
      kmlManager.onFileSelection({ target: { files } }, (polygons) =>
        onAreas(polygons?.dummy_id?.areas || []),
      );
    }
  };
  return (
    <div
      className={'ns-drop-zone' + (disabled ? ' ns-disabled' : '')}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        readFiles(event.dataTransfer.files);
      }}
    >
      <div>{t('new_season_drop_kmz')}</div>
      <div className='ns-muted'>{t('new_season_or')}</div>
      <button
        type='button'
        className='ns-link-btn'
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
      >
        {t('new_season_browse')}
      </button>
      <input
        ref={inputRef}
        type='file'
        accept='.kml,.kmz'
        hidden
        onChange={(event) => {
          readFiles(event.target.files);
          event.target.value = '';
        }}
      />
    </div>
  );
};

const ListActions = ({
  selectedKeys,
  selectedField,
  onGroup,
  onUngroup,
  onDeleteField,
}) => {
  const { t } = useTranslation();
  const showUngroup = selectedField && selectedField.blocks.length > 1;
  if (selectedKeys.length < 2 && !selectedField) {
    return null;
  }
  return (
    <div className='ns-list-actions'>
      {selectedKeys.length >= 2 && (
        <button
          type='button'
          className='ns-btn ns-btn-outline ns-btn-small'
          onClick={onGroup}
        >
          {t('new_season_group')}
        </button>
      )}
      {showUngroup && (
        <button
          type='button'
          className='ns-btn ns-btn-outline ns-btn-small'
          onClick={onUngroup}
        >
          {t('new_season_ungroup')}
        </button>
      )}
      {selectedField && (
        <button
          type='button'
          className='ns-btn ns-btn-outline ns-btn-small'
          onClick={onDeleteField}
        >
          {t('new_season_delete_field')}
        </button>
      )}
    </div>
  );
};

const useLayoutHandlers = ({ updateDraft, selection, setDeleteKey, t }) => {
  const blockPrefix = t('new_season_block_prefix');
  const fieldPrefix = t('new_season_field_prefix');
  return useMemo(
    () => ({
      onSelectBlock: selection.selectBlock,
      onSelectField: selection.selectField,
      onRenameBlock: (key, name) =>
        updateDraft((draft) => layout.renameBlock(draft, key, name)),
      onRenameField: (key, name) =>
        updateDraft((draft) => layout.renameField(draft, key, name)),
      onMoveBlock: (blockKey, fieldKey) =>
        blockKey &&
        updateDraft((draft) => layout.moveBlock(draft, blockKey, fieldKey)),
      onRequestDeleteBlock: (key) => setDeleteKey(key),
      onAreasDrawn: (areasList) =>
        updateDraft((draft) =>
          areasList.reduce(
            (current, areas) =>
              layout.addDrawnBlock(current, [areas], blockPrefix).draft,
            draft,
          ),
        ),
      onAreasEdited: (key, areas) =>
        updateDraft((draft) => layout.setBlockAreas(draft, key, areas)),
      onAddField: () =>
        updateDraft((draft) => layout.addEmptyField(draft, fieldPrefix)),
      onGroup: () => {
        updateDraft((draft) =>
          layout.groupBlocks(draft, selection.selectedKeys, fieldPrefix),
        );
        selection.clear();
      },
      onUngroup: () => {
        updateDraft((draft) =>
          layout.ungroupField(draft, selection.selectedFieldKey),
        );
        selection.clear();
      },
      onDeleteField: () => {
        updateDraft((draft) =>
          layout.deleteField(draft, selection.selectedFieldKey),
        );
        selection.clear();
      },
    }),
    [updateDraft, selection, setDeleteKey, blockPrefix, fieldPrefix],
  );
};

const useSaveLayout = ({
  distributionId,
  farmingUnitId,
  draft,
  reloadTree,
  showToast,
  t,
}) => {
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = useState(false);
  const save = () => {
    if (layout.findInvalidName(draft)) {
      showToast(t('new_season_invalid_names'), 'error');
      return;
    }
    setIsSaving(true);
    Promise.resolve(
      dispatch(
        save_new_season_layout(
          distributionId,
          farmingUnitId,
          layout.buildLayoutBody(draft),
        ),
      ),
    )
      .then(() => {
        setIsSaving(false);
        showToast(t('new_season_changes_saved'));
      })
      .catch((error) => {
        setIsSaving(false);
        if (error?.saved) {
          showToast(
            t('new_season_saved_reload_failed', { message: error.message }),
            'error',
          );
          reloadTree();
        } else {
          showToast(error?.message || String(error), 'error');
        }
      });
  };
  return { isSaving, save };
};

const EditorNotes = ({ tree, canWriteRole }) => {
  const { t } = useTranslation();
  if (!tree.hierarchy_writes_enabled) {
    return <div className='ns-note'>{t('new_season_writes_disabled')}</div>;
  }
  return canWriteRole ? null : (
    <div className='ns-note'>{t('new_season_read_only')}</div>
  );
};

const FarmingUnitEditor = ({
  distributionId,
  farmingUnitId,
  tree,
  reloadTree,
  setDirty,
}) => {
  const { t } = useTranslation();
  const farmingUnit = useFarmingUnit(distributionId, farmingUnitId);
  const [draft, updateDraft] = useDraft(tree, setDirty);
  const selection = useSelection();
  const [deleteKey, setDeleteKey] = useState(null);
  const [drawMode, setDrawMode] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { toast, showToast } = useToast();
  const canWriteRole = WRITE_ROLES.includes(tree.role);
  const canEdit = Boolean(tree.hierarchy_writes_enabled) && canWriteRole;
  const handlers = useLayoutHandlers({
    updateDraft,
    selection,
    setDeleteKey,
    t,
  });
  const { isSaving, save } = useSaveLayout({
    distributionId,
    farmingUnitId,
    draft,
    reloadTree,
    showToast,
    t,
  });
  useDeleteKey(selection.selectedKeys, canEdit, setDeleteKey);

  const mapBlocks = useMemo(() => layout.allBlocks(draft), [draft]);
  const selectedField = draft.fields.find(
    (field) => field.key === selection.selectedFieldKey,
  );
  const editableKey =
    canEdit && selection.selectedKeys.length === 1
      ? selection.selectedKeys[0]
      : null;

  const confirmDelete = () => {
    const location = layout.findBlockLocation(draft, deleteKey);
    updateDraft((current) => layout.deleteBlock(current, deleteKey));
    selection.clear();
    setDeleteKey(null);
    if (location) {
      showToast(t('new_season_block_deleted', { name: location.block.name }));
    }
  };

  const tabs = [
    { value: 'meta', label: t('new_season_tab_meta_data'), disabled: true },
    { value: 'fields', label: t('new_season_tab_fields') },
    { value: 'sensors', label: t('new_season_tab_sensors'), disabled: true },
  ];

  return (
    <div className='ns-editor'>
      <div className='ns-card ns-left-card'>
        <div className='ns-card-title'>
          {t('new_season_manage_farming_unit')}
        </div>
        <SegmentedTabs options={tabs} value='fields' onChange={() => {}} />
        <KmzDropZone disabled={!canEdit} onAreas={handlers.onAreasDrawn} />
        <ListActions
          selectedKeys={canEdit ? selection.selectedKeys : []}
          selectedField={canEdit ? selectedField : null}
          onGroup={handlers.onGroup}
          onUngroup={handlers.onUngroup}
          onDeleteField={handlers.onDeleteField}
        />
        <div className='ns-list-scroll'>
          <BlockList
            draft={draft}
            selectedKeys={selection.selectedKeys}
            selectedFieldKey={selection.selectedFieldKey}
            readOnly={!canEdit}
            handlers={handlers}
          />
        </div>
        <button
          type='button'
          className='ns-btn ns-btn-outline ns-btn-block'
          disabled={!canEdit}
          onClick={handlers.onAddField}
        >
          {t('new_season_add_field')}
        </button>
      </div>
      <div className='ns-right-column'>
        <div className='ns-card ns-right-card'>
          <button
            type='button'
            className='ns-title-bar'
            onClick={() => setIsEditModalOpen(true)}
          >
            {farmingUnitTitle(farmingUnit)}
          </button>
          <BlocksMap
            className='ns-map-large'
            fitKey={distributionId + '/' + farmingUnitId}
            blocks={mapBlocks}
            selectedKeys={selection.selectedKeys}
            onBlockClick={selection.selectBlock}
            showDrawTools={canEdit}
            drawMode={drawMode}
            onDrawModeChange={setDrawMode}
            onDrawn={(areas) => handlers.onAreasDrawn([areas[0]])}
            editableKey={editableKey}
            onAreasEdited={handlers.onAreasEdited}
          />
        </div>
        <div className='ns-save-row'>
          <EditorNotes tree={tree} canWriteRole={canWriteRole} />
          <button
            type='button'
            className='ns-btn ns-btn-primary'
            disabled={!canEdit || isSaving}
            onClick={save}
          >
            {t('new_season_save_changes')}
            {isSaving && (
              <span
                className='spinner-border spinner-border-sm ml-2'
                role='status'
              />
            )}
          </button>
        </div>
      </div>
      <ConfirmDeleteBlockModal
        isOpen={Boolean(deleteKey)}
        onCancel={() => setDeleteKey(null)}
        onConfirm={confirmDelete}
      />
      <FarmingUnitFormModal
        isOpen={isEditModalOpen && Boolean(farmingUnit)}
        mode='edit'
        distributionId={distributionId}
        farmingUnit={farmingUnit}
        onClose={() => setIsEditModalOpen(false)}
        onCreated={() => {}}
      />
      <Toast toast={toast} />
    </div>
  );
};

module.exports = FarmingUnitEditor;
